// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Table.tsx
 * @input React, StyleX, BaseTable, theme tokens, types, components
 * @output Exports Table component, TableProps, TableDensity, TableDividers types
 * @position Styled wrapper; the primary table API for consumers
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Table/Table.doc.mjs (props table, features, usage examples)
 * - /packages/core/src/Table/Table.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/Table/index.ts (exports if types change)
 * - /apps/storybook/stories/Table.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Table/ (showcase blocks)
 */
import { useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars } from "../theme/tokens.stylex.js";
import { BaseTable } from "./BaseTable.js";
import { TableContext } from "./TableContext.js";
import { useBaseTablePlugins } from "./useBaseTablePlugins.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

// =============================================================================
// Table Types
// =============================================================================

/** Row density controlling padding and font size */

/** Divider style between cells */

/** How body cell text behaves when it exceeds column width */

/**
 * Props for the styled Table component.
 * Supports both data-driven mode and children mode with TableRow/Cell.
 *
 * @template T - The row data type
 */
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
// StyleX Styles (table-level only; cell/row/header styles owned by components)
// =============================================================================

const tableStyles = {
  base: {
    kMv6JI: "xjb2p0i",
    kMwMTN: "x1tgivj0",
    $$css: true
  }
};
const scrollWrapperStyles = {
  base: {
    kXHlph: "xw2csxc",
    kuHK5b: "x5lxg6s",
    $$css: true
  },
  containerBleed: {
    keTefX: "xojxgvx",
    k71WvV: "x1fcf3bl",
    kzqmXN: "xx6qvi6",
    keoZOQ: "xkibk3",
    k1K539: "xlayyun",
    $$css: true
  }
};
function TableScrollWrapper({
  children,
  htmlProps,
  xstyle: pluginStyles,
  beforeTable,
  afterTable
}) {
  const t = useTranslator();
  const {
    ref,
    ...restHtmlProps
  } = htmlProps ?? {};
  return /*#__PURE__*/_jsxs("div", {
    ref: ref
    // Keyboard-focusable so keyboard users can scroll a horizontally
    // overflowing table. Uses role="group" (not "region") so multiple
    // tables on a page don't create duplicate same-named landmarks
    // (axe: landmark-unique). Callers may override role/aria-label via
    // htmlProps.
    ,
    tabIndex: 0,
    role: "group",
    "aria-label": t('@astryx.table.label'),
    ...restHtmlProps,
    ...mergeProps(themeProps('table-scroll-wrapper'), stylex.props(scrollWrapperStyles.base, scrollWrapperStyles.containerBleed, ...(pluginStyles ?? []))),
    children: [beforeTable, children, afterTable]
  });
}

// =============================================================================
// Table-level styling plugin (only transforms the <table> element)
// =============================================================================

function buildTableStylePlugin() {
  return {
    transformTable(props) {
      const existingClass = props.htmlProps.className ?? '';
      const tableClass = themeProps('table').className;
      return {
        ...props,
        htmlProps: {
          ...props.htmlProps,
          className: existingClass ? `${existingClass} ${tableClass}` : tableClass
        },
        xstyle: [...props.xstyle, tableStyles.base]
      };
    }
  };
}

// =============================================================================
// Table Component
// =============================================================================

function TableInner({
  density = 'balanced',
  dividers = 'rows',
  isStriped = false,
  hasHover = false,
  verticalAlign = 'middle',
  textOverflow = 'wrap',
  plugins: userPlugins,
  columns,
  data,
  ref,
  ...rest
}) {
  // Table-level styling plugin (just adds font/color to <table>)
  const tablePlugin = useMemo(() => buildTableStylePlugin(), []);
  const basePlugins = useMemo(() => [tablePlugin], [tablePlugin]);

  // Convert named plugin record to stable memoized array
  const mergedPlugins = useBaseTablePlugins(basePlugins, userPlugins);
  const contextValue = useMemo(() => ({
    density,
    dividers,
    isStriped,
    hasHover,
    verticalAlign,
    textOverflow
  }), [density, dividers, isStriped, hasHover, verticalAlign, textOverflow]);
  return /*#__PURE__*/_jsx(TableContext, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(BaseTable, {
      ref: ref,
      data: data,
      columns: columns,
      plugins: mergedPlugins,
      textOverflow: textOverflow,
      scrollWrapper: TableScrollWrapper,
      ...rest
    })
  });
}

/**
 * Table — a styled, data-driven table component.
 *
 * Wraps BaseTable with styled components (TableRow, TableCell,
 * TableHeaderCell) that read appearance configuration from TableContext.
 * Density, dividers, striped rows, and hover effects are applied via
 * design tokens in the component styles.
 *
 * @compositionHint Use renderCell on columns to compose rich cell content.
 * Combine with Badge (status labels), StatusDot (colored indicators),
 * Text (formatted values), Avatar (user cells), and HStack/VStack
 * (multi-element cell layouts). Without renderCell, cells render as plain text.
 * Always set explicit width on columns using proportional() or pixel() — omitting
 * width skips the minimum width floor, which can cause columns to collapse on mobile.
 *
 * @example
 * ```
 * <Table
 *   data={users}
 *   columns={[
 *     { key: 'name', header: 'Name', width: proportional(1), renderCell: (u) => (
 *       <HStack gap={2} align="center">
 *         <Avatar name={u.name} size="small" />
 *         <Text weight="semibold">{u.name}</Text>
 *       </HStack>
 *     )},
 *     { key: 'status', header: 'Status', width: proportional(1), renderCell: (u) => (
 *       <Badge variant={u.active ? 'success' : 'error'} label={u.active ? 'Active' : 'Inactive'} />
 *     )},
 *   ]}
 *   density="compact"
 *   dividers="grid"
 *   hasHover
 * />
 * ```
 */
export const Table = TableInner;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
Table.displayName = 'Table';